import { useReducer } from "react";
import { resolveLayerSource } from "./layerSource";
import type { MapLayer } from "./mapLayer";

export type EnabledLayerIds = ReadonlySet<string>;
export type ErroredLayerIds = ReadonlySet<string>;

/** Errors live beside the toggles so a late tile error replays after the toggle that switched its layer off. */
export type LayerToggleState = {
  enabledIds: EnabledLayerIds;
  /** Always a subset of `enabledIds`. */
  erroredIds: ErroredLayerIds;
};

type LayerToggleAction =
  | { type: "toggle"; id: string }
  | { type: "replace"; ids: Iterable<string> }
  | { type: "error"; id: string }
  | { type: "success"; id: string };

/** Raster bands sharing one source URL are one image: they draw, toggle and list as one. */
export function imageKey(layer: MapLayer): string {
  return layer.kind === "raster" ? resolveLayerSource(layer.source) : layer.id;
}

function linkedLayerIds(layers: readonly MapLayer[], layer: MapLayer) {
  const key = imageKey(layer);
  return layers.filter((l) => imageKey(l) === key).map((l) => l.id);
}

function toggleEnabled(
  layers: readonly MapLayer[],
  enabled: EnabledLayerIds,
  layer: MapLayer,
  ids: readonly string[],
): EnabledLayerIds {
  const next = new Set(enabled);

  if (enabled.has(layer.id)) {
    for (const id of ids) next.delete(id);
    return next;
  }

  if (layer.role === "background") {
    for (const l of layers) if (l.role === "background") next.delete(l.id);
  }
  if (layer.kind !== "wfs" && layer.selectionGroup) {
    for (const candidate of layers) {
      if (
        candidate.kind !== "wfs" &&
        candidate.selectionGroup === layer.selectionGroup
      )
        next.delete(candidate.id);
    }
  }
  for (const id of ids) next.add(id);
  return next;
}

export function reduceLayerToggles(
  layers: readonly MapLayer[],
  state: LayerToggleState,
  action: LayerToggleAction,
): LayerToggleState {
  switch (action.type) {
    case "error": {
      const { enabledIds, erroredIds } = state;
      if (!enabledIds.has(action.id) || erroredIds.has(action.id)) return state;
      return { enabledIds, erroredIds: new Set(erroredIds).add(action.id) };
    }
    case "success": {
      if (!state.enabledIds.has(action.id) || !state.erroredIds.has(action.id))
        return state;
      const erroredIds = new Set(state.erroredIds);
      erroredIds.delete(action.id);
      return { enabledIds: state.enabledIds, erroredIds };
    }
    case "replace": {
      const enabledIds = new Set(action.ids);
      const erroredIds = new Set(
        [...state.erroredIds].filter((id) => enabledIds.has(id)),
      );
      return { enabledIds, erroredIds };
    }
    case "toggle": {
      const layer = layers.find((l) => l.id === action.id);
      if (!layer) return state;

      const ids = linkedLayerIds(layers, layer);
      const enabledIds = toggleEnabled(layers, state.enabledIds, layer, ids);
      const erroredIds = new Set(
        [...state.erroredIds].filter(
          (id) => enabledIds.has(id) && !ids.includes(id),
        ),
      );
      return { enabledIds, erroredIds };
    }
  }
}

export function useLayerToggles(
  layers: readonly MapLayer[],
  initialIds: () => Iterable<string> = () => [],
) {
  const [{ enabledIds, erroredIds }, dispatch] = useReducer(
    (state: LayerToggleState, action: LayerToggleAction) =>
      reduceLayerToggles(layers, state, action),
    undefined,
    (): LayerToggleState => ({
      enabledIds: new Set(initialIds()),
      erroredIds: new Set(),
    }),
  );

  return {
    enabledIds,
    erroredIds,
    toggleLayer: (id: string) => dispatch({ type: "toggle", id }),
    resetLayers: () => dispatch({ type: "replace", ids: [] }),
    replaceLayers: (ids: Iterable<string>) =>
      dispatch({ type: "replace", ids }),
    reportLayerError: (id: string) => dispatch({ type: "error", id }),
    reportLayerSuccess: (id: string) => dispatch({ type: "success", id }),
  };
}
